/* global BigInt */
import {useState} from 'react';
import {formatDate} from './workspaceFormatting';
import './PlainData.css';

// Saved provider and review records, read as short "Label: value" lines instead of JSON.
// One pass (prepareRecord) decides everything a reader sees — labels, values and currency — and
// both PlainData and the evidence browser draw from it. Long lists fold behind "Show N more"; nothing is cut.

const ACRONYMS={id:'ID',ids:'IDs',url:'URL',urls:'URLs',cpa:'CPA',cpc:'CPC',cpm:'CPM',ctr:'CTR',cvr:'CVR',roas:'ROAS',utm:'UTM',ga4:'GA4',
  api:'API',sku:'SKU',gclid:'GCLID',uuid:'UUID',http:'HTTP',json:'JSON',csv:'CSV',sha256:'SHA-256',pmax:'PMax',rsa:'RSA',kpi:'KPI',utc:'UTC'};
const SHOWN=8,INLINE_LIMIT=80;
const ENUM=/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/,CAPS_WORD=/^[A-Z]{5,}$/,DAY=/^\d{4}-\d{2}-\d{2}$/;
// Recorded times keep their clock time, fraction and zone exactly as written; only the date part is reworded.
const TIME=/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(Z|[+-]\d{2}:?\d{2})?$/;
const CURRENCY_KEYS=new Set(['currency','currencyCode','currency_code']);
const KIND={number:'Number',string:'Text',boolean:'Yes or no',null:'Empty'};

const words=text=>String(text).replace(/([a-z0-9])([A-Z])/g,'$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g,'$1 $2')
  .split(/[\s_\-.]+/).filter(Boolean).map(word=>word.toLowerCase());
const sentence=list=>{const text=list.map(word=>ACRONYMS[word]||word).join(' ');return text?text[0].toUpperCase()+text.slice(1):'';};
const isMicros=key=>key!=null&&/micros$/i.test(String(key))&&words(key).length>1;
const group=digits=>digits.replace(/\B(?=(\d{3})+(?!\d))/g,',');
const isLeaf=value=>value===null||typeof value!=='object';

// A finite number as plain decimal text, exactly as JavaScript prints it, without exponent notation.
function decimal(value){
  if(Number.isInteger(value))return BigInt(value).toString();
  const text=String(value),match=text.match(/^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/);
  if(!match)return text;
  const [,sign,whole,fraction='',exponent]=match,digits=whole+fraction,point=whole.length+Number(exponent);
  const body=point<=0?`0.${'0'.repeat(-point)}${digits}`:point>=digits.length?digits+'0'.repeat(point-digits.length):`${digits.slice(0,point)}.${digits.slice(point)}`;
  return sign+body.replace(/^0+(?=\d)/,'');
}
const grouped=text=>{const [whole,fraction]=text.replace('-','').split('.');return `${text.startsWith('-')?'-':''}${group(whole)}${fraction?`.${fraction}`:''}`;};

// Micros shift six places as decimal text, so no precision is lost at any size. Null when the value is not a plain number.
function fromMicros(value){
  const text=typeof value==='string'?value.trim():typeof value==='number'&&Number.isFinite(value)?decimal(value):null;
  if(text==null||!/^-?\d+(?:\.\d+)?$/.test(text))return null;
  const sign=text.startsWith('-')?'-':'',[whole,decimals='']=text.replace('-','').split('.'),digits=whole.padStart(7,'0');
  const units=digits.slice(0,-6).replace(/^0+(?=\d)/,''),fraction=(digits.slice(-6)+decimals).replace(/0+$/,'');
  return `${sign}${group(units)}${fraction?`.${fraction}`:''}`;
}
// A field converts when its value — unwrapped, or every value in its list — is a plain number.
// When it does not, none of its values convert, so the "micros" label always matches what is shown.
const converts=value=>{const single=unwrap(value);return single?converts(single.value):Array.isArray(value)?value.length>0&&value.every(converts):fromMicros(value)!=null;};

function money(units,currency){
  if(!currency)return units;
  let symbol=currency;
  try{symbol=new Intl.NumberFormat(undefined,{style:'currency',currency}).formatToParts(0).find(part=>part.type==='currency')?.value||currency;}catch{/* unknown code: show it as written */}
  const sign=units.startsWith('-')?'-':'',amount=units.replace('-','');
  return /^[A-Za-z]/.test(symbol)?`${sign}${symbol} ${amount}`:`${sign}${symbol}${amount}`;
}

// "micros" leaves the label only when the value was actually converted to units.
export function plainLabel(key,value){
  const list=words(key);
  return sentence(isMicros(key)&&converts(value)?list.filter(word=>word!=='micros'):list)||String(key);
}

export function plainValue(value,key,currency){
  if(value===undefined||value===null)return 'Not available';
  if(typeof value==='boolean')return value?'Yes':'No';
  if(isMicros(key)){const units=fromMicros(value);if(units!=null)return money(units,currency);}
  if(typeof value==='number')return Number.isFinite(value)?grouped(decimal(value)):String(value);
  if(typeof value!=='string')return plainText(value,key,currency);
  if(value==='')return 'Blank';
  if(ENUM.test(value))return sentence(value.toLowerCase().split('_'));
  if(CAPS_WORD.test(value))return value[0]+value.slice(1).toLowerCase();
  const time=value.match(TIME);
  if(time&&formatDate(time[1])!=='Not available')return `${formatDate(time[1])} ${time[2]}${time[3]?` ${time[3]==='Z'?'UTC':`UTC${time[3]}`}`:''}`;
  if(DAY.test(value)&&formatDate(value)!=='Not available')return formatDate(value);
  return value;
}

// Providers wrap list entries as {text: ...} or {asset: ...}; that single value is the entry.
// Only these wrapper keys: a real field such as {primaryStatus: ...} keeps its label.
const WRAPPERS=new Set(['text','asset','value','name','label','url']);
const unwrap=item=>{
  if(isLeaf(item)||Array.isArray(item))return null;
  const entries=Object.entries(item);
  return entries.length===1&&WRAPPERS.has(entries[0][0])&&isLeaf(entries[0][1])?{value:entries[0][1],name:entries[0][0]}:null;
};

// Every currency an object states for itself (currency, currencyCode, currency_code). Two different
// codes are a conflict: nothing inside that object gets a symbol, whatever the field order.
const statedCurrencies=value=>isLeaf(value)||Array.isArray(value)?[]:[...new Set(Object.entries(value)
  .filter(([key,item])=>CURRENCY_KEYS.has(key)&&typeof item==='string'&&/^[A-Z]{3}$/.test(item)).map(([,item])=>item))];
const CONFLICT=Symbol('conflicting currencies');
const ownCurrency=value=>{const codes=statedCurrencies(value);return codes.length>1?CONFLICT:codes[0]||null;};
const enclosing=(value,outer)=>{const own=ownCurrency(value);return own||outer;};
const usable=currency=>currency===CONFLICT?null:currency;
// The one currency named anywhere in a record, used where no own or enclosing currency is stated.
export function recordCurrency(value){
  const found=new Set();
  const walk=item=>{if(isLeaf(item))return;statedCurrencies(item).forEach(code=>found.add(code));(Array.isArray(item)?item:Object.values(item)).forEach(walk);};
  walk(value);
  return found.size===1?[...found][0]:null;
}

const TITLE_KEYS=['name','title','label','text'];
/**
 * The reader's view of a record: a tree of
 *   {leaf:true, key, index, wrapper, label, text, raw, kind}  one "Label: value" line (label null for a bare list
 *       entry); raw is the recorded value and wrapper the folded field ("text" in {text: ...}), both kept for search
 *   {leaf:false, key, index, label, list, children}          a heading with its own lines
 */
export function prepareRecord(value){
  const fallback=recordCurrency(value);
  // `convert` is false inside a micros list that does not convert as a whole, so no value in it is converted either.
  const leaf=(key,index,label,item,field,currency,convert,wrapper=null)=>({leaf:true,key,index,wrapper,label,
    text:plainValue(item,convert===false?undefined:field,currency===undefined||currency===null?fallback:usable(currency)),raw:String(item),kind:KIND[item===null?'null':typeof item]||'Text'});
  const walk=(item,key,index,label,currency,convert)=>{
    if(isLeaf(item))return leaf(key,index,label,item,key,currency,convert);
    const single=unwrap(item);
    if(single)return leaf(key,index,label,single.value,key??single.name,currency,convert,single.name);
    const here=enclosing(item,currency);
    if(Array.isArray(item)){
      const whole=convert===false?false:isMicros(key)?converts(item):undefined;
      const children=item.map((entry,position)=>walk(entry,key,position,isLeaf(entry)||unwrap(entry)?null:`Item ${position+1}`,here,whole));
      children.forEach(child=>{if(!child.leaf){const title=child.children.find(row=>row.leaf&&TITLE_KEYS.includes(row.key)&&row.text.trim());if(title)child.label=title.text;}});
      return {leaf:false,key,index,label,list:true,children};
    }
    const children=Object.entries(item).map(([name,entry])=>walk(entry,name,null,plainLabel(name,entry),here));
    return {leaf:false,key,index,label,list:false,children};
  };
  return walk(value,null,null,null,null);
}

// List entries join with commas, or semicolons when an entry has its own comma ("1,000,000; 2,000,000").
const separator=children=>children.some(child=>child.leaf&&child.text.includes(','))?'; ':', ';

// One line, for a single value in a cell that cannot hold a list. Structured values use the same
// prepared record as PlainData; nested groups sit in brackets: "Budget: (Period: Daily · Amount: 5)".
export function plainText(value,key,currency){
  if(isLeaf(value))return plainValue(value,key,usable(currency));
  const line=node=>node.leaf?(node.label==null?node.text:`${node.label}: ${node.text}`)
    :node.children.length?node.children.map(child=>child.leaf?line(child):`${child.label==null?'':`${child.label}: `}(${line({...child,label:null})})`).join(node.list?separator(node.children):' · '):'None';
  return line({...prepareRecord(value),label:null});
}

// Each line is one phrase, "Label: value", so it reads (and is found) as a sentence fragment rather than a bare value.
function Line({node}){
  if(node.leaf)return node.label==null?node.text:`${node.label}: ${node.text}`;
  if(node.list&&node.children.length&&node.children.every(child=>child.leaf&&child.label==null)){
    const inline=`${node.label}: ${node.children.map(child=>child.text).join(separator(node.children))}`;
    if(node.children.length<=SHOWN&&inline.length<=INLINE_LIMIT)return inline;
  }
  return <><span className="ow-plain-label">{node.label}:</span><List node={node}/></>;
}

function List({node}){
  if(!node.children.length)return <ul className="ow-plain-data"><li>None</li></ul>;
  const items=rows=>rows.map(child=><li key={child.key??child.index}><Line node={child}/></li>);
  return <ul className="ow-plain-data">{items(node.children.slice(0,SHOWN))}
    {node.children.length>SHOWN&&<More count={node.children.length-SHOWN}>{()=>items(node.children.slice(SHOWN))}</More>}</ul>;
}

// Folded rows are built only once opened: a long record would otherwise put thousands of nodes on the page unseen.
function More({count,children}){
  const [open,setOpen]=useState(false);
  return <li className="ow-plain-more"><details onToggle={event=>setOpen(event.currentTarget.open)}><summary>Show {count} more</summary>
    {open&&<ul className="ow-plain-data">{children()}</ul>}</details></li>;
}

export default function PlainData({value}){
  const node=prepareRecord(value);
  if(node.leaf)return <p className="ow-plain-data">{node.text}</p>;
  return <div className="ow-plain-data-root"><List node={node}/></div>;
}
